import { useState } from 'react';
import { Trans } from 'react-i18next';
import { addWaitlistSubscription } from '../../openapi/reservations/reservations';
import { useGetTimeslots } from '../../openapi/timeslots/timeslots';
import { ShowSuccessResponseResponse } from '../../openapi/model';
import ContentHeader from '../components/ContentHeader';
import Footer from '../components/Footer';
import MobileBottomBar from '../components/MobileBottomBar';
import NextToEvent from '../components/NextToEvent';
import IconCheckmark from '../icons/IconCheckmark';
import Steps from '../components/Steps';
import { useCart } from '../hooks/useCart';
import { useLang } from '../hooks/useLanguage';
import { setLanguage } from '../../openapi/custom-axios-instance';
import { formatDate } from '../helpers/formatters';
import { EMAIL_REGEX } from '../helpers/validation';
import { getNormalizedShowId } from '../helpers/show';
import { computeTicketStatuses } from './computeTicketStatuses';
import TimeslotCancelledBanner from '../components/TimeslotCancelledBanner';

function WaitlistScreen({
    show,
    forceSoldOutStatus = false,
    onBackToTickets,
    onViewMoreDates,
}: Readonly<{
    show: ShowSuccessResponseResponse;
    forceSoldOutStatus?: boolean;
    onBackToTickets: () => void;
    onViewMoreDates: () => void;
}>) {
    const { cart, timeslot, genericBookingFields, setGenericBookingFields } = useCart();
    const { t, getI18n, language } = useLang();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const showId = getNormalizedShowId(show);

    const { data: { timeslots: [timeslotData] = [] } = { timeslots: [] } } = useGetTimeslots(
        {
            idList: timeslot?.id ? [timeslot.id] : [],
            includeBlocks: true,
        },
        {
            query: {
                enabled: Boolean(timeslot?.id),
            },
        },
    );

    const ticketStatuses = computeTicketStatuses(
        cart,
        timeslotData?.ticketTypeBlocks || [],
        forceSoldOutStatus,
        getI18n,
    );

    const cancellationReason = timeslotData?.cancellationReason;
    const isCancelled = Boolean(cancellationReason && cancellationReason.trim());

    const waitlistSidebarStep = 3;
    const submittedTicketSummary = ticketStatuses
        .map((item) => `${item.quantity} ${item.ticketName}`)
        .join(', ');
    const submittedDate = timeslot ? formatDate(timeslot.startTime, t('widgetLocale')) : '-';

    const isEmailInvalid =
        genericBookingFields.email.length > 0 && !EMAIL_REGEX.test(genericBookingFields.email);

    const isWaitlistFormInvalid =
        cart.length === 0 ||
        !genericBookingFields.firstName ||
        !genericBookingFields.lastName ||
        !genericBookingFields.email ||
        !EMAIL_REGEX.test(genericBookingFields.email) ||
        !timeslot;

    const submitWaitlist = async () => {
        if (isWaitlistFormInvalid || !timeslot) {
            return;
        }

        setIsSubmitting(true);
        setSubmitError(null);

        try {
            setLanguage(language);
            await addWaitlistSubscription({
                showId: showId ?? 0,
                timeslotId: timeslot.id,
                genericBookingFields: {
                    firstName: genericBookingFields.firstName,
                    lastName: genericBookingFields.lastName,
                    email: genericBookingFields.email,
                    phone: genericBookingFields.phone || undefined,
                },
                cart: cart.map((item) => ({
                    ticketTypeId: item.ticketTypeId,
                    ticketOptionId: item.ticketOptionId,
                    quantity: item.quantity || 1,
                })),
            });
            setSubmitted(true);
        } catch (error: any) {
            const message = error?.response?.data?.message;
            setSubmitError(message || t('errors.createReservationError'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fireknife-step fireknife-waitlist">
            <div className="fireknife-step__main fireknife-waitlist__main">
                {!submitted && (
                    <div className="fireknife-waitlist__content-header">
                        <ContentHeader
                            prevStep={onBackToTickets}
                            label={t('widgetWaitlist.changeTickets')}
                        />
                    </div>
                )}

                {cancellationReason && cancellationReason.trim() && (
                    <TimeslotCancelledBanner reason={cancellationReason} />
                )}

                {!submitted && !isCancelled && (
                    <>
                        <h2 className="fireknife-booking-title">
                            {t('widgetWaitlist.unavailableTitle')}
                        </h2>

                        <div className="fireknife-waitlist__status-box">
                            <h3 className="fireknife-waitlist__status-title">
                                {t('widgetWaitlist.selectedTicketsStatus')}
                            </h3>
                            <div className="fireknife-waitlist__status-list">
                                {ticketStatuses.map((item) => (
                                    <div className="fireknife-waitlist__status-item" key={item.key}>
                                        <span className="fireknife-waitlist__status-name">
                                            {item.ticketName} ({item.quantity})
                                        </span>
                                        <span
                                            className={`fireknife-waitlist__status-badge ${
                                                item.status === 'soldOut'
                                                    ? 'fireknife-waitlist__status-badge--sold-out'
                                                    : 'fireknife-waitlist__status-badge--available'
                                            }`}
                                        >
                                            {item.status === 'soldOut'
                                                ? t('widgetWaitlist.statusSoldOut')
                                                : t('widgetWaitlist.statusAvailable')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="fireknife-waitlist__form-box">
                            <h3 className="fireknife-waitlist__form-title">
                                {t('widgetWaitlist.joinTitle')}
                            </h3>
                            <p className="fireknife-booking-desc">
                                {t('widgetWaitlist.joinDescription')}
                            </p>

                            <div className="fireknife-form-groups fireknife-waitlist__form-grid">
                                {[
                                    {
                                        id: 'firstName',
                                        name: 'firstName' as const,
                                        type: 'text',
                                        labelKey: 'widgetBookingInformation.firstName',
                                        fullRow: false,
                                        required: true,
                                    },
                                    {
                                        id: 'lastName',
                                        name: 'lastName' as const,
                                        type: 'text',
                                        labelKey: 'widgetBookingInformation.lastName',
                                        fullRow: false,
                                        required: true,
                                    },
                                    {
                                        id: 'email',
                                        name: 'email' as const,
                                        type: 'email',
                                        labelKey: 'widgetBookingInformation.email',
                                        fullRow: true,
                                        hasError: isEmailInvalid,
                                        required: true,
                                    },
                                    {
                                        id: 'phone',
                                        name: 'phone' as const,
                                        type: 'tel',
                                        labelKey: 'widgetBookingInformation.phone',
                                        fullRow: true,
                                        required: false,
                                    },
                                ].map((field) => (
                                    <div
                                        key={field.id}
                                        className={`fireknife-form-group ${
                                            field.fullRow
                                                ? 'fireknife-waitlist__form-group--full-row'
                                                : ''
                                        }`}
                                    >
                                        <label
                                            className={`fireknife-form-label ${
                                                field.required
                                                    ? 'fireknife-form-label--required'
                                                    : ''
                                            }`}
                                            htmlFor={`fireknife-waitlist-${field.id}`}
                                        >
                                            {t(field.labelKey)}
                                        </label>
                                        <div
                                            className={`fireknife-form-control-box ${
                                                field.hasError
                                                    ? 'fireknife-form-control-box--error'
                                                    : ''
                                            }`}
                                        >
                                            <input
                                                className="fireknife-form-control"
                                                id={`fireknife-waitlist-${field.id}`}
                                                type={field.type}
                                                name={field.name}
                                                value={genericBookingFields[field.name]}
                                                onChange={(e) =>
                                                    setGenericBookingFields({
                                                        ...genericBookingFields,
                                                        [field.name]: e.target.value,
                                                    })
                                                }
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {submitError && (
                            <div className="fireknife-waitlist__submit-error">{submitError}</div>
                        )}
                    </>
                )}

                {submitted && (
                    <div className="fireknife-waitlist__success">
                        <div className="fireknife-waitlist__success-icon-box">
                            <span className="fireknife-waitlist__success-icon-circle">
                                <IconCheckmark className="fireknife-waitlist__success-icon" />
                            </span>
                        </div>
                        <h2 className="fireknife-booking-title fireknife-waitlist__success-title">
                            {t('widgetWaitlist.successTitle')}
                        </h2>
                        <p className="fireknife-waitlist__success-desc">
                            <Trans
                                i18nKey="widgetWaitlist.successDescriptionRich"
                                shouldUnescape={true}
                                values={{
                                    tickets: submittedTicketSummary,
                                    date: submittedDate,
                                    email: genericBookingFields.email,
                                }}
                                components={{
                                    strong: <strong />,
                                    email: <span className="fireknife-waitlist__success-email" />,
                                }}
                            />
                        </p>
                        <div className="fireknife-waitlist__success-actions">
                            <button
                                className="fireknife-btn fireknife-btn--primary"
                                type="button"
                                onClick={onViewMoreDates}
                            >
                                {t('widgetWaitlist.returnHome')}
                            </button>
                        </div>
                    </div>
                )}

                <Footer />
            </div>

            <aside className="fireknife-step__aside fireknife-step4__aside">
                {!submitted && !isCancelled && (
                    <MobileBottomBar
                        showFees={show.fees}
                        isBreakingOutFees={show.isBreakingOutFees}
                        priceListingMode={show.priceListingMode}
                        cardProcessingFee={show.cardProcessingFee}
                        nextStep={submitWaitlist}
                        label={t('widgetWaitlist.joinButton')}
                        disabled={isWaitlistFormInvalid || isSubmitting}
                    />
                )}

                <div className="fireknife-step__aside-content">
                    <div className="fireknife-step4__payment">
                        <Steps
                            step={waitlistSidebarStep}
                            setStep={() => {}}
                            showId={showId}
                            forceShowSeatsStep={true}
                            hideSeatsStep={false}
                            rentalMode={!!show?.marketing?.rentalMode}
                        >
                            {!submitted && !isCancelled && (
                                <p className="fireknife-waitlist__step-note">
                                    {t('widgetWaitlist.waitlistAvailable')}
                                </p>
                            )}
                            {submitted && (
                                <p className="fireknife-waitlist__step-note fireknife-waitlist__step-note--success">
                                    {t('widgetWaitlist.requestSubmitted')}
                                </p>
                            )}
                        </Steps>
                    </div>

                    {submitted && (
                        <div className="fireknife-waitlist__submitted-box">
                            <div className="fireknife-waitlist__submitted-label">
                                {t('widgetWaitlist.waitlistStatusLabel')}
                            </div>
                            <div className="fireknife-waitlist__submitted-badge-row">
                                <span className="fireknife-waitlist__status-pill fireknife-waitlist__status-pill--submitted">
                                    {t('widgetWaitlist.pendingAvailability')}
                                </span>
                            </div>
                            <p className="fireknife-waitlist__aside-footnote">
                                {t('widgetWaitlist.noPaymentProcessed')}
                            </p>
                        </div>
                    )}
                </div>

                {!submitted && !isCancelled && (
                    <NextToEvent
                        nextStep={submitWaitlist}
                        label={t('widgetWaitlist.joinButton')}
                        disabled={isWaitlistFormInvalid || isSubmitting}
                        showFees={show.fees}
                        isBreakingOutFees={show.isBreakingOutFees}
                        priceListingMode={show.priceListingMode}
                        cardProcessingFee={show.cardProcessingFee}
                        totalNote={
                            <div className="fireknife-waitlist__total-note">
                                {t('widgetWaitlist.noPaymentRequired')}
                            </div>
                        }
                    />
                )}
            </aside>
        </div>
    );
}

export default WaitlistScreen;
